import React, { FC, ReactText, useEffect } from 'react';
import FormGroup from 'components/form/FormGroup';
import Form from 'components/form/Form';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import API from 'core/Api';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { useWizardContext } from 'store/wizard';
import { useModal } from 'store/modal';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { ISelectOptionProps } from 'components/form/types';
import { useQueryClient } from 'react-query';
import Wizard from '../../components/Wizard';
import WizardNavAndSubmit from '../../components/WizardNavAndSubmit';
import { formStepsInAndEx, formStepsExternal } from '../../wizardItems';
import { IInputs } from './types';

interface ISegmentSelectOptionProps extends ISelectOptionProps {
  label_en: string;
}

interface ISegment {
  segment_id: number;
  segment_name: string;
  segment_name_en?: string;
  options: ISegmentSelectOptionProps[];
}

interface IProps {
  data?: any;
}

const StepTwo: FC<IProps> = ({ data }: IProps) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const history = useHistory();
  const { showModal } = useModal();
  const { client } = useActiveClientContext();
  const { currentStep, setCurrentStep } = useWizardContext();
  const { segments, type } = data;
  const queryClient = useQueryClient();

  const buildDefaultValues = () => {
    const defaultValues: any = {};
    Object.keys(segments).forEach((key: string) => {
      defaultValues[`segmentId-${segments[key].segment_id}`] = segments[key].options.find(
        (opt: ISegmentSelectOptionProps) => opt.selected
      );
    });
    return defaultValues;
  };

  const methods = useForm<(fieldName: ReactText, error: { type: string; message: string }) => void>({
    defaultValues: buildDefaultValues(),
  });
  const { formState, reset, setError } = methods;
  const { isSubmitting } = formState;

  const handleSelectChange = (e: any) => {
    localStorage.removeItem('formValues');
  };

  useEffect(() => {
    reset(buildDefaultValues());
  }, [data]);

  const onSubmit = (submitData: IInputs) => {
    const segmentData: {
      segment_id: number;
      element_id: string;
    }[] = [];
    Object.keys(submitData).forEach(key => {
      const slicedKey = key.split('-');
      segmentData.push({
        segment_id: Number(slicedKey[1]),
        element_id: submitData[key].value,
      });
    });

    return API.post('document-wizard/step-two', {
      segment_data: segmentData,
    })
      .then(res => {
        const { next_step: nextStep } = res.data;
        // Set next current step into wizard context
        setCurrentStep(nextStep);
        // Push to next step
        const url = buildClientUrl(`/documents-wizard/step-${nextStep}`, client);

        queryClient.removeQueries(buildClientEntity('step-two', client));
        queryClient.removeQueries(buildClientEntity('step-three', client));
        history.push(url);
      })
      .catch(res => {
        const response = createErrorFromResponse(res);
        setServerErrors(response.errors, setError);
      });
  };

  return (
    <>
      <Wizard
        currentStep={currentStep}
        wizardItems={type === 'internal' ? formStepsInAndEx : formStepsExternal}
      />
      <Row className="mt-4">
        <Col md={{ offset: 2, span: 8 }}>
          <Form
            methods={methods}
            onSubmit={onSubmit}
            btnLabel={t('common.saveAndContinue')}
            isHorizontal
            hideCancelBtn
            areBtnsCentered
          >
            <WizardNavAndSubmit
              currentStep={currentStep}
              backStep={1}
              isSubmitting={isSubmitting}
            />
            {segments.map((segment: ISegment) => {
              return (
                <FormGroup
                  controlId={`formGroup${segment.segment_name}`}
                  label={language === 'en' && segment.segment_name_en ? segment.segment_name_en : segment.segment_name}
                  type="select"
                  as="select"
                  name={`segmentId-${segment.segment_id}`}
                  isRequired
                  isClearable
                  isSearchable
                  isHorizontal
                  labelGrid={3}
                  controlGrid={7}
                  onChange={handleSelectChange}
                  selectOptions={segment.options.map((opt: ISegmentSelectOptionProps) => ({
                    ...opt,
                    label_en: null,
                    label: language === 'en' && opt.label_en ? opt.label_en : opt.label,
                  }))}
                />
              );
            })}
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default StepTwo;
